import {
  EventTemplate,
  TemplateSectionType,
} from "@/types/EventTemplate";
import { apiDelete, apiGet, apiPost, apiPostFormData, apiPut } from "../_apiShim";
import { withAiContentTool } from "@/lib/ai/aiContentQuery";
import type { CreateEventResponse } from "./EventClient";
import { EventRoleCreationDTO } from "@/types/EventRoleCreationDTO";
import type { RegistrationType } from "@/types/enums/RegistrationType";
import type { Plugin } from "@/types/enums/Plugin";

const BASE_PATH = "/api/v1/event-templates";

/**
 * Wizard overrides for POST /{templateId}/apply. Absent (undefined) fields
 * fall back to the template's config server-side; PRESENT fields are
 * authoritative — including blank location strings (explicit clear, the
 * wizard's Ort="Nein") and an empty plugins list (all deselected).
 */
export interface ApplyTemplateOverrides {
  name: string;
  start: string;
  end: string;
  timeZone: string;
  description?: string;
  expectedCapacity?: number;
  onlineUrl?: string;
  location?: {
    locationName: string;
    address: string;
    address2: string;
    zipCode: string;
    city: string;
    country: string;
  };
  registrationType?: RegistrationType;
  plugins?: Plugin[];
  /** Copy this event's guest list into the new event (state reset, fresh
   *  codes) — server-side, best-effort, outcome in CreateEventResponse. */
  guestSourceEventId?: string;
}

export async function createTemplate(
  template: Partial<EventTemplate>,
): Promise<string> {
  return await apiPost<string>(
    BASE_PATH,
    template,
    {
      "Cache-Control": "no-store",
    },
  );
}

export async function saveEventAsTemplate(
  eventId: string,
  name: string,
  description: string,
  includedSections: TemplateSectionType[],
): Promise<EventTemplate> {
  return await apiPost<EventTemplate>(
    `${BASE_PATH}/from-event/${eventId}`,
    { name, description, includedSections },
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

export async function getTemplates(
  page: number,
  size: number,
  searchTerm: string,
): Promise<EventTemplate[]> {
  const params = new URLSearchParams({
    page: page.toString(),
    size: size.toString(),
  });
  if (searchTerm) params.append("searchTerm", searchTerm);

  return await apiGet<EventTemplate[]>(
    `${BASE_PATH}?${params.toString()}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

export async function getTemplate(
  templateId: string,
): Promise<EventTemplate> {
  return await apiGet<EventTemplate>(
    `${BASE_PATH}/${templateId}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

export async function getTemplateSection(
  templateId: string,
  section: TemplateSectionType,
): Promise<Partial<EventTemplate>> {
  return await apiGet<Partial<EventTemplate>>(
    `${BASE_PATH}/${templateId}?sections=${section}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}

export async function updateTemplate(
  templateId: string,
  updates: Partial<EventTemplate>,
): Promise<void> {
  await apiPut<void>(
    `${BASE_PATH}/${templateId}`,
    updates,
    {
      "Cache-Control": "no-store",
    },
  );
}

export async function updateTemplateSection(
  templateId: string,
  section: TemplateSectionType,
  data: unknown,
): Promise<void> {
  await apiPut<void>(
    `${BASE_PATH}/${templateId}/sections/${section}`,
    data as Record<string, unknown>,
    {
      "Cache-Control": "no-store",
    },
  );
}

export async function deleteTemplate(
  templateId: string,
): Promise<void> {
  await apiDelete<void>(
    `${BASE_PATH}/${templateId}`,
    {
      "Cache-Control": "no-store",
    },
  );
}

// Apply template — single multipart request. Mirrors POST /api/v1/events/{companyId}
// (scratch path): one call handles event creation, extra collaborator invites,
// and the optional title image atomically. Response shape matches the scratch
// path so handleCreateEvent can handle both paths uniformly.
export async function applyTemplate(
  templateId: string,
  overrides: ApplyTemplateOverrides,
  extraCollaborators: EventRoleCreationDTO[] = [],
  image?: { buffer: ArrayBuffer; fileName: string; mimeType: string },
  /**
   * Art. 50 Abs. 2 KI-VO: the AI lane that wrote wizard values into this
   * create (`apply_event_wizard_state`). Query parameter only — the multipart
   * body is read by a strict `ObjectMapper` that refuses unknown fields. The
   * server stamps the new event's guest-content mark after the copy.
   */
  aiContentTool?: string,
): Promise<CreateEventResponse> {
  const formData = new FormData();
  formData.append(
    "body",
    new Blob(
      [
        JSON.stringify({
          ...overrides,
          extraCollaborators,
        }),
      ],
      { type: "application/json" },
    ),
  );
  if (image) {
    formData.append(
      "image",
      new Blob([image.buffer], { type: image.mimeType }),
      image.fileName,
    );
  }
  return await apiPostFormData<CreateEventResponse>(
    withAiContentTool(`${BASE_PATH}/${templateId}/apply`, aiContentTool),
    formData,
    {
      "Cache-Control": "no-store",
    },
  );
}

export async function getEventSectionPreview(
  eventId: string,
  section: TemplateSectionType,
): Promise<unknown> {
  return await apiGet<unknown>(
    `${BASE_PATH}/import-preview/${eventId}/${section}`,
    {
      "Cache-Control": "no-store",
    },
    true,
  );
}
